import Toast from "components/Toast";
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { Message } from "semantic-ui-react";
import { getTranslatedUserMsg } from "strings";
import { useT } from "i18n/i18n";

export type UIMessageProps = {
  title: string;
  msg: string;
  icon?: string;
  kind?: MessageKind;
};

enum MessageKind {
  INFO,
  ERROR,
  SUCCESS,
}

interface UIContextType {
  devMode: boolean;
  setDevMode: (active: boolean) => void;
  showInfo: (msg: UIMessageProps) => void;
  showError: (msg?: UIMessageProps) => void;
  showSuccess: (msg?: UIMessageProps) => void;
}

const UIContext = createContext<UIContextType>({
  devMode: false,
  setDevMode: () => {
    return;
  },
  showInfo: () => {
    return;
  },
  showError: () => {
    return;
  },
  showSuccess: () => {
    return;
  },
});

export function useUI() {
  return useContext(UIContext);
}

export function UIProvider({ children }: { children?: ReactNode }): ReactElement {
  const t = useT();
  const [devMode, setDevMode] = useState(false);
  const [message, setMessage] = useState({} as UIMessageProps);

  const showInfo = useCallback((msg: UIMessageProps) => {
    setMessage(msg);
  }, []);
  const showError = useCallback(
    (msg = getTranslatedUserMsg(t, "ERROR_GENERIC") as UIMessageProps) => {
      setMessage({ ...msg, kind: MessageKind.ERROR });
    },
    [t],
  );
  const showSuccess = useCallback(
    (msg = getTranslatedUserMsg(t, "SUCCESS_GENERIC") as UIMessageProps) => {
      setMessage({ ...msg, kind: MessageKind.SUCCESS });
    },
    [t],
  );

  useEffect(() => {
    if (devMode)
      setTimeout(() => {
        showSuccess(getTranslatedUserMsg(t, "DEV_MODE"));
      }, 0);
  }, [devMode, showSuccess, t]);

  return (
    <UIContext.Provider
      value={{ devMode, setDevMode, showInfo, showError, showSuccess }}
    >
      {children}
      <Toast trigger={message}>
        <UIMessage {...message} />
      </Toast>
    </UIContext.Provider>
  );
}

export function UIMessage({ title, msg, icon, kind }: UIMessageProps): ReactElement {
  const isLiteralIcon = icon && icon.length < 5;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {isLiteralIcon && (
        <h1
          style={{
            fontSize: "4em",
            margin: 0,
          }}
        >
          {icon}
        </h1>
      )}
      <Message
        compact
        error={kind === MessageKind.ERROR}
        success={kind === MessageKind.SUCCESS}
        icon={isLiteralIcon ? undefined : icon}
        size={"large"}
        header={title}
        content={msg}
      />
    </div>
  );
}

export const MountedStyle = { animation: "inAnimation 250ms ease-in" };

export const UnmountedStyle = {
  animation: "outAnimation 270ms ease-out",
  animationFillMode: "forwards",
};
